import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { RiSearchLine, RiAddLine, RiArrowRightSLine, RiArrowUpSLine, RiArrowDownSLine } from "@remixicon/react";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import StatusPill from "../common/StatusPill";
import EmptyState from "../common/EmptyState";
import MarketSwitcher from "../common/MarketSwitcher";
import { getOrdersByType } from "../../api/bids";
import { orderNumber, formatGallons, statusLabel, formatDeliveryWindow } from "../../utils/status";

const TABS = [
	{ key: "bidRequest", label: "Bid requests", empty: "Bids you request appear here while sellers respond." },
	{ key: "openOrder", label: "Open orders", empty: "Orders you've awarded to a seller appear here." },
	{ key: "ordersInProgress", label: "In progress", empty: "Deliveries that are on the way appear here." },
	{ key: "pastOrder", label: "Past orders", empty: "Delivered orders appear here." },
	{ key: "closedOrders", label: "Cancelled", empty: "Cancelled orders appear here." },
];

const SORT_VALUE = {
	order: (o) => Number(o.orderId) || orderNumber(o),
	location: (o) => (o.locationDetails?.locationName || o.locationDetails?.streetAddress || "").toLowerCase(),
	product: (o) => (o.productType?.[0]?.Title || o.product || "").toLowerCase(),
	quantity: (o) => Number(o.orderSize) || 0,
	delivery: (o) => (o.deliveryDate ? new Date(o.deliveryDate).getTime() : 0),
	status: (o) => statusLabel(o).toLowerCase(),
};

const COLUMNS = [
	{ key: "order", label: "Order #" },
	{ key: "location", label: "Location" },
	{ key: "product", label: "Product" },
	{ key: "quantity", label: "Quantity" },
	{ key: "delivery", label: "Delivery date / time" },
	{ key: "status", label: "Status" },
];

export default function OrdersList() {
	const location = useLocation();
	const navigate = useNavigate();
	// A freshly submitted bid request lands in the "bidRequest" bucket, not "openOrder"
	// (see STATUS_INFO/OPEN_ORDER_STATUS_LIST in config.js) — NewBidRequest and the
	// sidebar summary navigate here with state.tab so the right bucket is shown.
	const [tab, setTab] = useState(location.state?.tab || "bidRequest");
	const [loading, setLoading] = useState(true);
	const [orders, setOrders] = useState([]);
	const [query, setQuery] = useState("");
	const [sort, setSort] = useState({ key: null, dir: "asc" });

	useEffect(() => {
		if (location.state?.tab) setTab(location.state.tab);
	}, [location.state]);

	useEffect(() => {
		(async () => {
			setLoading(true);
			const res = await getOrdersByType(tab);
			setOrders(res && !res.error ? res.data || [] : []);
			setLoading(false);
		})();
	}, [tab]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return orders;
		return orders.filter((o) =>
			[orderNumber(o), o.locationDetails?.locationName, o.productType?.[0]?.Title, statusLabel(o)].some((v) => String(v || "").toLowerCase().includes(q))
		);
	}, [orders, query]);

	const sorted = useMemo(() => {
		if (!sort.key) return filtered;
		const val = SORT_VALUE[sort.key];
		const factor = sort.dir === "asc" ? 1 : -1;
		return [...filtered].sort((a, b) => {
			const x = val(a);
			const y = val(b);
			if (x === y) return 0;
			return (x > y ? 1 : -1) * factor;
		});
	}, [filtered, sort]);

	const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

	const current = TABS.find((t) => t.key === tab);

	return (
		<div>
			<MarketSwitcher />
			<PageHeader
				title="Orders"
				subtitle="Track every bid request from submission through delivery"
				actions={
					<Link to="/bids/new" className="btn-accent-solid">
						<RiAddLine size={18} /> New bid request
					</Link>
				}
			/>

			<ul className="nav nav-tabs mb-3">
				{TABS.map((t) => (
					<li className="nav-item" key={t.key}>
						<button className={`nav-link ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)}>
							{t.label}
						</button>
					</li>
				))}
			</ul>

			<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
				<span className="text-muted small">{loading ? "" : `${filtered.length} ${filtered.length === 1 ? "order" : "orders"}`}</span>
				<div className="search-input">
					<RiSearchLine size={16} />
					<input className="form-control" placeholder="Search order #, location, product" value={query} onChange={(e) => setQuery(e.target.value)} />
				</div>
			</div>

			{loading ? (
				<Loader />
			) : filtered.length === 0 ? (
				<EmptyState
					title={query ? "No matching orders" : `No ${current.label.toLowerCase()}`}
					subtitle={query ? "Try a different search." : current.empty}
					action={
						!query && tab === "bidRequest" ? (
							<Link to="/bids/new" className="btn-accent-solid">
								Request a bid
							</Link>
						) : null
					}
				/>
			) : (
				<div className="table-responsive">
					<table className="table table-hover align-middle">
						<thead>
							<tr>
								{COLUMNS.map((c) => (
									<th key={c.key} role="button" onClick={() => toggleSort(c.key)} style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}>
										{c.label}
										{sort.key === c.key ? sort.dir === "asc" ? <RiArrowUpSLine size={16} /> : <RiArrowDownSLine size={16} /> : null}
									</th>
								))}
								<th className="d-none d-md-table-cell" />
							</tr>
						</thead>
						<tbody>
							{sorted.map((o) => (
								// Backend's bidRequest detail lookup matches the custom `id` field, not Mongo's `_id`.
								<tr key={o._id || o.id} role="button" onClick={() => navigate(`/orders/${o.id || o._id}`)}>
									<td>
										<span className="order-link">{orderNumber(o)}</span>
									</td>
									<td>{o.locationDetails?.locationName || o.locationDetails?.streetAddress || "-"}</td>
									<td>{o.productType?.[0]?.Title || o.product || "-"}</td>
									<td>{formatGallons(o.orderSize)}</td>
									<td>
										{o.deliveryDate ? moment(o.deliveryDate).format("MM/DD/YYYY") : "-"}
										{o.deliveryTime ? <span className="text-muted"> · {formatDeliveryWindow(o.deliveryTime)}</span> : null}
									</td>
									<td>
										<StatusPill order={o} />
									</td>
									<td className="text-end text-muted d-none d-md-table-cell">
										<RiArrowRightSLine size={18} />
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
